import { useCallback, useEffect, useRef, useState, KeyboardEvent as ReactKeyboardEvent } from 'react';

/**
 * Types for keyboard navigation
 */
export type KeyboardNavigationKeys = 
  | 'ArrowUp' 
  | 'ArrowDown' 
  | 'ArrowLeft' 
  | 'ArrowRight'
  | 'Enter'
  | 'Escape'
  | 'Tab'
  | 'Space'
  | 'Home'
  | 'End';

/**
 * Hook for managing focus trap within a container.
 * Keeps focus contained within a specific element (useful for modals, dialogs).
 * 
 * @example
 * ```tsx
 * function Modal({ isOpen }) {
 *   const containerRef = useFocusTrap(isOpen);
 *   return (
 *     <div ref={containerRef} role="dialog">
 *       <button>Close</button>
 *       <input type="text" />
 *     </div>
 *   );
 * }
 * ```
 */
export function useFocusTrap<T extends HTMLElement = HTMLElement>(
  isActive: boolean = true,
  options: {
    /** Element to return focus to when trap is deactivated */
    returnFocusTo?: HTMLElement | null;
    /** Auto-focus first focusable element when activated */
    autoFocus?: boolean;
    /** Allow escape key to deactivate trap */
    escapeDeactivates?: boolean;
    /** Callback when escape is pressed (only if escapeDeactivates is true) */
    onEscape?: () => void;
  } = {}
) {
  const containerRef = useRef<T>(null);
  const {
    returnFocusTo,
    autoFocus = true,
    escapeDeactivates = false,
    onEscape,
  } = options;

  useEffect(() => {
    if (!isActive || !containerRef.current) return;

    const container = containerRef.current;
    const previouslyFocused = returnFocusTo || (document.activeElement as HTMLElement);

    // Get all focusable elements
    const getFocusableElements = (): HTMLElement[] => {
      const selector = [
        'button:not([disabled])',
        'input:not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        'a[href]',
        '[tabindex]:not([tabindex="-1"])',
      ].join(', ');
      
      return Array.from(container.querySelectorAll<HTMLElement>(selector)).filter(
        (el) => el.offsetParent !== null // Check visibility
      );
    };

    // Auto-focus first element
    if (autoFocus) {
      const focusable = getFocusableElements();
      if (focusable.length > 0) {
        focusable[0].focus();
      }
    }

    // Handle tab key for focus trap
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && escapeDeactivates) {
        onEscape?.();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = getFocusableElements();
      if (focusable.length === 0) return;

      const firstElement = focusable[0];
      const lastElement = focusable[focusable.length - 1];

      if (event.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    };

    container.addEventListener('keydown', handleKeyDown);

    return () => {
      container.removeEventListener('keydown', handleKeyDown);
      // Return focus when trap is deactivated
      if (previouslyFocused && 'focus' in previouslyFocused) {
        previouslyFocused.focus();
      }
    };
  }, [isActive, returnFocusTo, autoFocus, escapeDeactivates, onEscape]);

  return containerRef;
}

/**
 * Hook for roving tabindex pattern in lists/menus.
 * Only one item is focusable at a time via Tab; arrow keys move focus.
 * 
 * @example
 * ```tsx
 * function Menu({ items }) {
 *   const { focusedIndex, getItemProps, containerProps } = useRovingFocus(items.length);
 *   
 *   return (
 *     <ul {...containerProps}>
 *       {items.map((item, index) => (
 *         <li key={item.id} {...getItemProps(index)}>
 *           {item.label}
 *         </li>
 *       ))}
 *     </ul>
 *   );
 * }
 * ```
 */
export function useRovingFocus(
  itemCount: number,
  options: {
    /** Initial focused index */
    initialIndex?: number;
    /** Orientation for arrow keys */
    orientation?: 'horizontal' | 'vertical' | 'both';
    /** Wrap around when reaching edges */
    wrap?: boolean;
    /** Callback when focus changes */
    onFocusChange?: (index: number) => void;
    /** Callback when item is selected (Enter/Space) */
    onSelect?: (index: number) => void;
  } = {}
) {
  const {
    initialIndex = 0,
    orientation = 'vertical',
    wrap = true,
    onFocusChange,
    onSelect,
  } = options;

  const focusedIndexRef = useRef(initialIndex);
  const [focusedIndex, _setFocusedIndex] = useState(initialIndex);
  const itemRefs = useRef<Map<number, HTMLElement>>(new Map());

  const setFocusedIndex = useCallback(
    (index: number) => {
      focusedIndexRef.current = index;
      _setFocusedIndex(index);
      const element = itemRefs.current.get(index);
      if (element) {
        element.focus();
        onFocusChange?.(index);
      }
    },
    [onFocusChange]
  );

  const moveFocus = useCallback(
    (direction: 'next' | 'prev' | 'first' | 'last') => {
      const currentIndex = focusedIndexRef.current;
      let newIndex: number;

      switch (direction) {
        case 'next':
          newIndex = currentIndex + 1;
          if (newIndex >= itemCount) {
            newIndex = wrap ? 0 : itemCount - 1;
          }
          break;
        case 'prev':
          newIndex = currentIndex - 1;
          if (newIndex < 0) {
            newIndex = wrap ? itemCount - 1 : 0;
          }
          break;
        case 'first':
          newIndex = 0;
          break;
        case 'last':
          newIndex = itemCount - 1;
          break;
      }

      setFocusedIndex(newIndex);
    },
    [itemCount, wrap, setFocusedIndex]
  );

  const handleKeyDown = useCallback(
    (event: ReactKeyboardEvent) => {
      const { key } = event;

      switch (key) {
        case 'ArrowDown':
          if (orientation === 'vertical' || orientation === 'both') {
            event.preventDefault();
            moveFocus('next');
          }
          break;
        case 'ArrowUp':
          if (orientation === 'vertical' || orientation === 'both') {
            event.preventDefault();
            moveFocus('prev');
          }
          break;
        case 'ArrowRight':
          if (orientation === 'horizontal' || orientation === 'both') {
            event.preventDefault();
            moveFocus('next');
          }
          break;
        case 'ArrowLeft':
          if (orientation === 'horizontal' || orientation === 'both') {
            event.preventDefault();
            moveFocus('prev');
          }
          break;
        case 'Home':
          event.preventDefault();
          moveFocus('first');
          break;
        case 'End':
          event.preventDefault();
          moveFocus('last');
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          onSelect?.(focusedIndexRef.current);
          break;
      }
    },
    [orientation, moveFocus, onSelect]
  );

  const getItemProps = useCallback(
    (index: number) => ({
      ref: (element: HTMLElement | null) => {
        if (element) {
          itemRefs.current.set(index, element);
        } else {
          itemRefs.current.delete(index);
        }
      },
      tabIndex: index === focusedIndex ? 0 : -1,
      onFocus: () => {
        focusedIndexRef.current = index;
        _setFocusedIndex(index);
      },
      onKeyDown: handleKeyDown,
    }),
    [handleKeyDown, focusedIndex]
  );

  const containerProps = {
    role: 'listbox' as const,
    'aria-activedescendant': undefined,
  };

  return {
    focusedIndex,
    setFocusedIndex,
    getItemProps,
    containerProps,
  };
}

/**
 * Hook for announcing dynamic content changes to screen readers.
 * 
 * @example
 * ```tsx
 * function SearchResults({ results }) {
 *   const announce = useAriaLive();
 *   
 *   useEffect(() => {
 *     announce(`Found ${results.length} results`);
 *   }, [results.length, announce]);
 *   
 *   return <ul>...</ul>;
 * }
 * ```
 */
export function useAriaLive(politeness: 'polite' | 'assertive' = 'polite') {
  const announce = useCallback(
    (message: string) => {
      // Find or create the live region
      let liveRegion = document.getElementById(`aria-live-${politeness}`);
      
      if (!liveRegion) {
        liveRegion = document.createElement('div');
        liveRegion.id = `aria-live-${politeness}`;
        liveRegion.setAttribute('aria-live', politeness);
        liveRegion.setAttribute('aria-atomic', 'true');
        liveRegion.setAttribute('role', politeness === 'assertive' ? 'alert' : 'status');
        liveRegion.className = 'sr-only';
        document.body.appendChild(liveRegion);
      }

      // Clear and set new message to ensure announcement
      liveRegion.textContent = '';
      
      // Use setTimeout to ensure the clearing is processed before the new content
      setTimeout(() => {
        if (liveRegion) {
          liveRegion.textContent = message;
        }
      }, 100);
    },
    [politeness]
  );

  return announce;
}

/**
 * Hook for managing skip links for keyboard navigation.
 * Creates a hidden link that becomes visible on focus.
 * 
 * @example
 * ```tsx
 * function Layout() {
 *   const { skipLinkProps, targetProps } = useSkipLink('Skip to main content');
 *   
 *   return (
 *     <>
 *       <a {...skipLinkProps} />
 *       <nav>...</nav>
 *       <main {...targetProps}>...</main>
 *     </>
 *   );
 * }
 * ```
 */
export function useSkipLink(label: string) {
  const targetRef = useRef<HTMLElement>(null);
  const skipLinkId = `skip-link-${label.toLowerCase().replace(/\s+/g, '-')}`;

  const handleClick = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    targetRef.current?.focus();
    targetRef.current?.scrollIntoView();
  }, []);

  const skipLinkProps = {
    href: `#${skipLinkId}`,
    className: 'skip-link',
    onClick: handleClick,
    children: label,
  };

  const targetProps = {
    ref: targetRef,
    id: skipLinkId,
    tabIndex: -1,
  };

  return { skipLinkProps, targetProps };
}

/**
 * Hook to detect and respect user's reduced motion preference.
 * 
 * @example
 * ```tsx
 * function AnimatedComponent() {
 *   const prefersReducedMotion = usePrefersReducedMotion();
 *   
 *   return (
 *     <div className={prefersReducedMotion ? 'no-animation' : 'animate'}>
 *       ...
 *     </div>
 *   );
 * }
 * ```
 */
export function usePrefersReducedMotion(): boolean {
  const mediaQuery = typeof window !== 'undefined' 
    ? window.matchMedia?.('(prefers-reduced-motion: reduce)')
    : null;

  const getMatches = useCallback(() => {
    return mediaQuery?.matches ?? false;
  }, [mediaQuery]);

  // Use useState so changes trigger a re-render
  const [matches, setMatches] = useState(getMatches());

  useEffect(() => {
    if (!mediaQuery) return;

    const handleChange = () => {
      setMatches(getMatches());
    };

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
    
    return undefined;
  }, [mediaQuery, getMatches]);

  return matches;
}

/**
 * Utility to generate unique IDs for accessibility attributes.
 * More predictable than React's useId for testing.
 */
let idCounter = 0;
export function generateA11yId(prefix: string = 'a11y'): string {
  return `${prefix}-${++idCounter}`;
}

/**
 * Resets the ID counter (useful for testing).
 */
export function resetA11yIdCounter(): void {
  idCounter = 0;
}
